import * as React from 'react';

const optionDefaults = {
    allowManualRetry: true,
    maxSize: 52428800,
    fromSources: [
        'local_file_system',
        // TODO: Re-enable cloud sources once Filestack integration issues are resolved
        // 'box',
        // 'dropbox',
        // 'googledrive',
        // 'imagesearch',
        'webcam',
    ],
} as const;

const multiSelectOptionDefaults = {
    ...optionDefaults,
    maxFiles: 50,
} as const;

type PickerCroppedData = {
    cropArea: {
        /**
         * [x, y]
         */
        position: [number, number];
        /**
         * [width, height]
         */
        size: [number, number];
    };
    /**
     * [width, height]
     */
    originalImageSize: [number, number];
};

type PickerRotatedData = {
    /**
     * Amount rotated in degrees.
     */
    value: number;
    /**
     * Can be CW or CCW (clockwise / counter-clockwise)
     */
    direction: 'CC' | 'CCW';
};

export type File = {
    /**
     * The cloud container for the uploaded file.
     */
    container?: string;
    /**
     * Position and size information for cropped images.
     */
    cropped?: PickerCroppedData;
    /**
     * Name of the file.
     */
    filename: string;
    /**
     * Filestack handle for the uploaded file.
     */
    handle: string;
    /**
     * The hash-prefixed cloud storage path.
     */
    key?: string;
    /**
     * The MIME type of the file.
     */
    mimetype: string;
    /**
     * Properties of the local binary file. Also see the pick option `exposeOriginalFile` if you want the underlying `File` object.
     */
    originalFile?: any | File;
    /**
     * The origin of the file, e.g. /Folder/file.jpg.
     */
    originalPath: string;
    /**
     * Direction and value information for rotated images.
     */
    rotated?: PickerRotatedData;
    /**
     * Size in bytes of the uploaded file.
     */
    size: number;
    /**
     * The source from where the file was picked.
     */
    source: string;
    /**
     * Indicates Filestack transit status.
     */
    status?: string;
    /**
     * A uuid for tracking this file in callbacks.
     */
    uploadId: string;
    /**
     * The Filestack CDN URL for the uploaded file.
     */
    url: string;
};

type Props = {
    isMultiSelect: boolean;
    options?: any;
    onSuccess: (arg1: {filesUploaded: File[]}) => void;
    render?: (arg1: {onPick: (...args: any) => any}) => any;
    // For managing the open state if using a button outside the component.
    // Ex: AttachmentsWidgetWrapper uses a button managed by CardWidget
    isOpen?: boolean;
    onClose?: () => void;
};

export function FilestackFilePicker(props: Props) {
    const {isMultiSelect = false, options, onSuccess, render, isOpen, onClose} = props;

    const uploadModalOpenRef = React.useRef<boolean>(false);

    const handlePick = React.useCallback(() => {
        const filestackOptions = isMultiSelect ? multiSelectOptionDefaults : optionDefaults;

        const mergedOptions = {
            ...filestackOptions,
            ...options,
        } as const;

        try {
            const filepickerClient = Nut.modules.Global.init.configureFilePicker();

            filepickerClient
                .picker({
                    ...mergedOptions,
                    onUploadDone: onSuccess,
                    // TODO: Remove this once https://github.com/filestack/filestack-js/issues/335 is fixed
                    // @ts-expect-error
                    onFileSelected: (file) => {
                        if (mergedOptions.maxSize && file.size > mergedOptions.maxSize) {
                            alert(
                                `Sorry, but the file you chose exceeds the maximum size of ${
                                    mergedOptions.maxSize / 1024 / 1024
                                }mb.`
                            );
                        }
                    },
                    // @ts-expect-error
                    onFileUploadFailed(file, error) {
                        // eslint-disable-next-line no-console
                        console.error('FilePicker error', error, 'for file', file);
                        // @ts-expect-error
                        if (typeof window.trackJs !== 'undefined') {
                            // @ts-expect-error
                            window.trackJs.track(
                                `FilePicker error: ${error.message} -- ${error.details} -- ${error.stack}`
                            );
                        }
                    },
                    onClose: () => {
                        if (onClose) {
                            onClose();
                        }
                    },
                })
                .open();
        } catch (error: any) {
            // eslint-disable-next-line no-console
            console.error(error.message, error.details);
            // @ts-expect-error
            if (typeof window.trackJs !== 'undefined') {
                // @ts-expect-error
                window.trackJs.track(`${error.message}: ${JSON.stringify(error.details)}`);
            }
        }
    }, [isMultiSelect, options, onSuccess, onClose]);

    React.useEffect(() => {
        if (isOpen && !uploadModalOpenRef.current) {
            handlePick();
        }
        uploadModalOpenRef.current = Boolean(isOpen);
    }, [isOpen, handlePick]);

    return render ? render({onPick: handlePick}) : null;
}
